import React, { useCallback } from 'react';
import { extractFilesFromRtfClipboard, renameClipboardImageFile, isClipboardFromExcel, isClipboardFromPowerPoint } from '../../helpers/clipboardHelper';

export const useTextareaPasteHandler = (onFilesPaste: undefined | ((files: File[]) => void), contentEditableRef: React.RefObject<HTMLDivElement>, textareaRef: React.RefObject<HTMLTextAreaElement>) => {
    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!contentEditableRef.current || !textareaRef.current || !onFilesPaste) {
            return;
        }

        contentEditableRef.current.focus();
        const items = e.clipboardData?.items || [];

        // Check what kind of content we have
        const hasStringItem = Array.from(items).some(item => item.kind === 'string');
        const hasFileItem = Array.from(items).some(item => item.kind === 'file');

        const isFromExcel = isClipboardFromExcel(e.clipboardData);
        const isFromPowerPoint = isClipboardFromPowerPoint(e.clipboardData);

        // If we have both text and files, and it's from Excel or PowerPoint, ignore the files
        if (hasStringItem && hasFileItem && (isFromExcel || isFromPowerPoint)) {
            textareaRef.current.focus();
            contentEditableRef.current.innerHTML = "";
            return;
        }

        // Handle files (either standalone files or Word content with embedded images)
        let files: File[] = [];

        // Get regular file items
        for (const item of items) {
            if (item.kind === "file") {
                const file = item.getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }

        // Extract embedded images from RTF content
        const refFiles = extractFilesFromRtfClipboard(e.clipboardData);
        files.push(...refFiles);

        textareaRef.current.focus();
        if (files.length) {
            onFilesPaste(files.map(renameClipboardImageFile));
        }
        contentEditableRef.current.innerHTML = "";
    }, [onFilesPaste, contentEditableRef, textareaRef]);

    return handlePaste;
};